import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { JobsStateJobFragment } from '@shared/contexts/pluginContexts/EmailThreadContext';
import {
  JobSummaryFragment,
  JobType,
  useGetJobsQuery,
} from '@shared/generated/graphql';
import { useJobPolling } from '@shared/hooks/useJobPolling';
import {
  getHasUnconfirmedQuoteAndOrder,
  jobFragmentsToJobsState,
} from '@shared/plugin/utils/optimisticJobs';
import { useMemo, useState } from 'react';

type GmailJobsState = {
  jobs: JobSummaryFragment[];
  jobTypes: Record<string, JobType>;
  storeJobType: (jobType: JobType, jobId?: string) => void;
  loading: boolean;
  hasUnconfirmedQuote: boolean;
  hasUnconfirmedOrder: boolean;
};

const POLL_INTERVAL_INFREQUENT_SECONDS = 30 * 1000;

type UseGmailJobsProps = { threadId: string };
export const useGmailJobs = (p: UseGmailJobsProps): GmailJobsState => {
  const [jobTypes, setJobTypes] = useState<Record<string, JobType>>({});
  const { defaultOrgId } = useMeContext();
  const { threadId } = p;
  const { data, startPolling, stopPolling, loading } = useGetJobsQuery({
    variables: {
      input: {
        organizationIds: defaultOrgId ? [defaultOrgId] : [],
        gmailFilter: { threadId },
        filterUnconfirmedJobs: false,
      },
    },
    skip: !threadId,
    pollInterval: POLL_INTERVAL_INFREQUENT_SECONDS,
  });
  useJobPolling({
    data,
    startPolling,
    stopPolling,
  });

  const storeJobType = (jobType: JobType, jobId?: string) => {
    if (!jobId) return;

    setJobTypes((prev) => ({
      ...prev,
      [jobId]: jobType,
    }));
  };

  const jobs: JobsStateJobFragment[] = useMemo(() => {
    return jobFragmentsToJobsState(data);
  }, [data]);

  const { hasUnconfirmedQuote, hasUnconfirmedOrder } = useMemo(() => {
    return getHasUnconfirmedQuoteAndOrder(data);
  }, [data]);

  return {
    jobs,
    jobTypes,
    storeJobType,
    loading,
    hasUnconfirmedOrder,
    hasUnconfirmedQuote,
  };
};
